import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { transformedValue, type FormValueControl } from '@angular/forms/signals';
import { UiFieldControl } from '../../../basics/ui-field-control';
import { IconUIComponent } from '../icon/icon.component';
import { UiFieldActionDirective } from '../../../directives';
import { IconsInitConfigType } from '../../../constants/icon.constants';
import { NumberRangeInputSizeType, NumberRangeModel } from '../../../types/number-range-input.type';

const MAX_BOUND_DIGITS = 9;
const RANGE_SEPARATOR = '-';

/** A `from-to` text field: `10-20`, `10-` (from only), `-20` (to only) or `15` (both ends). */
@Component({
  selector: 'fr8bot-ui-number-range-field',
  imports: [IconUIComponent, UiFieldActionDirective],
  templateUrl: './number-range-field.component.html',
  styleUrl: './number-range-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NumberRangeFieldComponent
  extends UiFieldControl
  implements FormValueControl<NumberRangeModel | null>
{
  readonly value = model<NumberRangeModel | null>(null);

  readonly label = input<string>();
  readonly placeholder = input<string>();
  readonly size = input<NumberRangeInputSizeType>('medium');
  readonly leftIcon = input<IconsInitConfigType>();
  readonly rightIcon = input<IconsInitConfigType>();

  // Keeps a half-typed `10-` on screen while the model already holds `{ from: 10 }`.
  protected readonly text = transformedValue<NumberRangeModel | null, string>(this.value, {
    parse: (text) => ({ value: parseRange(text) }),
    format: (value) => formatRange(value),
  });

  protected readonly isFilled = computed(() => this.text().length > 0);

  protected onInput(element: HTMLInputElement): void {
    const text = sanitize(element.value);

    element.value = text;
    this.text.set(text);
  }

  protected clear(): void {
    this.text.set('');
  }
}

/** Digits plus a single separating dash, each bound capped at `MAX_BOUND_DIGITS`. */
function sanitize(raw: string): string {
  const value = raw.replace(/[^0-9-]/g, '');
  const dashIndex = value.indexOf('-');

  if (dashIndex === -1) return value.slice(0, MAX_BOUND_DIGITS);

  const left = value.slice(0, dashIndex).slice(0, MAX_BOUND_DIGITS);
  const right = value
    .slice(dashIndex + 1)
    .replace(/-/g, '')
    .slice(0, MAX_BOUND_DIGITS);

  return `${left}-${right}`;
}

function parseRange(text: string): NumberRangeModel | null {
  if (text === '' || text === RANGE_SEPARATOR) return null;

  const dashIndex = text.indexOf('-');

  if (dashIndex === -1) {
    const bound = Number(text);
    return { from: bound, to: bound };
  }

  if (dashIndex === 0) return { to: Number(text.slice(1)) };
  if (dashIndex === text.length - 1) return { from: Number(text.slice(0, -1)) };

  return { from: Number(text.slice(0, dashIndex)), to: Number(text.slice(dashIndex + 1)) };
}

function formatRange(value: NumberRangeModel | null): string {
  const from = isBound(value?.from) ? value?.from : undefined;
  const to = isBound(value?.to) ? value?.to : undefined;

  if (from === undefined && to === undefined) return '';

  return from === to ? `${from}` : `${from ?? ''}-${to ?? ''}`;
}

function isBound(bound: unknown): bound is number {
  return typeof bound === 'number' && bound >= 0;
}
